import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { billingStore } from './Billing';
import { userStore } from './User';

const DEBUG_EMAIL_DOMAINS = ['ijw.fyi', 'starzero.ai'];

// Subscriptions whose members get the Story Atlas without any debug toggling.
const ATLAS_SUBSCRIPTION_PRODUCT_IDS = [
  'prod_U3VnUUEw5eiakx', // Friends & CO
  'prod_T3HnNhLsIwDj8X', // STAFF
];

const RESPECTFUL_DEBUG_MODE_KEY = 'respectfulDebugMode';
const DEMO_MODE_KEY = 'debug.bigSearchBar';
const FORCE_JSON_API_KEY = 'debug.forceJsonApi';
const MOCK_HLS_UNSUPPORTED_KEY = 'debug.mockHlsUnsupported';
const SMALL_BATCH_SIZE_MODE_KEY = 'debug.smallBatchSize';
const SIMULATE_PROCESSING_MODAL_KEY = 'debug.simulateProcessingModal';
const DEBUG_MEDIA_CARDS_KEY = 'debug.mediaCards';
const GRID_RANK_DEBUG_KEY = 'debug.gridRank';
const MOBILE_UI_KEY = 'debug.mobileUI';
const SHOW_CENTER_LINE_KEY = 'debug.showCenterLine';
const FORCE_FIRST_TIME_USER_KEY = 'debug.forceFirstTimeUser';
const UPLOAD_STATUS_KEY = 'debug.uploadStatus';
const FAKE_PODCAST_CLIP_COUNT_KEY = 'debug.fakePodcastClipCount';
const STORY_ATLAS_KEY = 'debug.storyAtlas';
const ENABLE_UNIFIED_SEARCH_KEY = 'debug.enableUnifiedSearch';
const PUBLISHING_ENTRY_POINTS_KEY = 'debug.publishingEntryPoints';

// FIXME(publishing-internal-only): while true, the publishing entry points
// (the Schedule nav row and the Settings Publishing tab) show only to team
// email domains, with an "internal testing" badge; the pages themselves stay
// reachable by URL. Acceptable once: the feature is being tested by the team
// before a public launch. Retire by deleting this constant, the
// `publishingEntryPoints` toggle below and `InternalTestingBadge`, and
// un-gating both surfaces; the vibedit nav mirror (`FIXME(nav-mirror)`)
// waits on that flip.
const PUBLISHING_INTERNAL_ONLY = true;

// TODO: Remove this override once the upload status panel has proven stable.
// While true, `showUploadStatus` is forced on for all users. To re-gate the
// feature behind debug mode, set this to `false` — the DebugTab toggle and
// the `debug.uploadStatus` localStorage entry are intentionally kept so
// re-gating is a one-line revert.
const FORCE_UPLOAD_STATUS_ENABLED = true;

export class DebugStore {
  private crossStoreReactionsBound = false;

  @observable
  private clickTimestamps: number[] = [];

  @observable
  respectfulDebugMode: boolean;

  @observable
  demoModeValue: boolean;

  @observable
  forceJsonApi: boolean;

  @observable
  mockHlsUnsupported: boolean;

  @observable
  smallBatchSizeMode: boolean;

  @observable
  simulateProcessingModal: boolean;

  @observable
  mediaCardsDebugMode: boolean;

  @observable
  gridRankDebugMode: boolean;

  @observable
  mobileUI: boolean;

  @observable
  showCenterLine: boolean;

  @observable
  forceFirstTimeUser: boolean;

  @observable
  uploadStatus: boolean;

  /** When non-null, the Workspace "Your podcast clips" card uses this many fake
   * projects instead of the real fetch, so we can preview every count (0 = empty /
   * hidden, 1 = resume cover only, 2+ = resume + recents strip) without real data. */
  @observable
  fakePodcastClipCount: number | null;

  @observable
  storyAtlas: boolean;

  /** Unified search is disabled product-wide (see `showUnifiedSearch`); this
   * debug toggle brings it back for local testing / a future re-launch. */
  @observable
  enableUnifiedSearch: boolean;

  /** Debug preview of the public view: off hides the publishing entry points from a team member. On by default. */
  @observable
  publishingEntryPoints: boolean;

  private timeout: NodeJS.Timeout | null = null;

  constructor() {
    this.respectfulDebugMode = localStorage.getItem(RESPECTFUL_DEBUG_MODE_KEY) === 'true';
    this.demoModeValue = localStorage.getItem(DEMO_MODE_KEY) === 'true';
    this.forceJsonApi = localStorage.getItem(FORCE_JSON_API_KEY) === 'true';
    this.mockHlsUnsupported = localStorage.getItem(MOCK_HLS_UNSUPPORTED_KEY) === 'true';
    this.smallBatchSizeMode = localStorage.getItem(SMALL_BATCH_SIZE_MODE_KEY) === 'true';
    this.simulateProcessingModal = localStorage.getItem(SIMULATE_PROCESSING_MODAL_KEY) === 'true';
    this.mediaCardsDebugMode = localStorage.getItem(DEBUG_MEDIA_CARDS_KEY) === 'true';
    this.gridRankDebugMode = localStorage.getItem(GRID_RANK_DEBUG_KEY) === 'true';
    const storedMobileUI = localStorage.getItem(MOBILE_UI_KEY);
    this.mobileUI = storedMobileUI === null ? true : storedMobileUI === 'true';
    this.showCenterLine = localStorage.getItem(SHOW_CENTER_LINE_KEY) === 'true';
    this.forceFirstTimeUser = localStorage.getItem(FORCE_FIRST_TIME_USER_KEY) === 'true';
    this.uploadStatus = localStorage.getItem(UPLOAD_STATUS_KEY) === 'true';
    const storedFakeClipCount = localStorage.getItem(FAKE_PODCAST_CLIP_COUNT_KEY);
    const parsedFakeClipCount = storedFakeClipCount === null ? NaN : Number(storedFakeClipCount);
    // Number.isFinite guards against a garbage localStorage value (NaN → treat as
    // "not set" rather than letting NaN flow through to Array.from).
    this.fakePodcastClipCount = Number.isFinite(parsedFakeClipCount) ? parsedFakeClipCount : null;
    this.storyAtlas = localStorage.getItem(STORY_ATLAS_KEY) === 'true';
    this.enableUnifiedSearch = localStorage.getItem(ENABLE_UNIFIED_SEARCH_KEY) === 'true';
    this.publishingEntryPoints = localStorage.getItem(PUBLISHING_ENTRY_POINTS_KEY) !== 'false';
    makeObservable(this);

    // Sync mobile-ui class on <html> for CSS @include mobile gating
    this.syncMobileUIClass();
    reaction(
      () => this.mobileUI,
      () => this.syncMobileUIClass(),
    );
  }

  /**
   * Bind the reaction that reads `userStore` — which sits in an import cycle
   * with this module. Called from src/index.tsx once the whole module graph
   * has evaluated; registering it during construction (even deferred to a
   * microtask) raced module initialization in production chunk orders and
   * threw "Cannot access 'X' before initialization".
   */
  initCrossStoreReactions() {
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;
    reaction(
      () => this.isTeamMember,
      (isTeamMember) => {
        if (isTeamMember && !this.respectfulDebugMode) {
          this.setRespectfulDebugMode(true);
        }
      },
      { fireImmediately: true },
    );
  }

  /**
   * Whether the signed-in user belongs to one of the team email domains.
   * `null` while the user has not loaded yet: a gate that redirects on a
   * `false` must wait for a real answer, because a full-page load (an OAuth
   * return, a bookmark) renders before `userStore.data` arrives.
   */
  @computed
  get isTeamMember(): boolean | null {
    if (!userStore.data) return null;
    const domain = userStore.data.email?.split('@')[1]?.toLowerCase();
    return !!domain && DEBUG_EMAIL_DOMAINS.includes(domain);
  }

  @computed
  get bigSearchBar(): boolean {
    return this.respectfulDebugMode && this.demoModeValue;
  }

  @computed
  get showMediaCardsDebug(): boolean {
    return this.respectfulDebugMode && this.mediaCardsDebugMode;
  }

  @computed
  get showGridRankDebug(): boolean {
    return this.respectfulDebugMode && this.gridRankDebugMode;
  }

  /** Whether unified search surfaces (mode toggle, URL param, workspace CTA,
   * copy that names the mode) are shown. Off for everyone by default — the
   * feature was pulled from the FE but kept behind this flag for a cheap
   * re-launch. */
  @computed
  get showUnifiedSearch(): boolean {
    return this.respectfulDebugMode && this.enableUnifiedSearch;
  }

  @action
  setRespectfulDebugMode(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(RESPECTFUL_DEBUG_MODE_KEY, 'true');
    } else {
      localStorage.removeItem(RESPECTFUL_DEBUG_MODE_KEY);
    }
    this.respectfulDebugMode = enabled;
  }

  @action
  setBigSearchBar(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(DEMO_MODE_KEY, 'true');
    } else {
      localStorage.removeItem(DEMO_MODE_KEY);
    }
    this.demoModeValue = enabled;
  }

  @action
  setForceJsonApi(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(FORCE_JSON_API_KEY, 'true');
    } else {
      localStorage.removeItem(FORCE_JSON_API_KEY);
    }
    this.forceJsonApi = enabled;
  }

  @action
  setMockHlsUnsupported(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(MOCK_HLS_UNSUPPORTED_KEY, 'true');
    } else {
      localStorage.removeItem(MOCK_HLS_UNSUPPORTED_KEY);
    }
    this.mockHlsUnsupported = enabled;
  }

  @action
  setSmallBatchSizeMode(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(SMALL_BATCH_SIZE_MODE_KEY, 'true');
    } else {
      localStorage.removeItem(SMALL_BATCH_SIZE_MODE_KEY);
    }
    this.smallBatchSizeMode = enabled;
  }

  @action
  setSimulateProcessingModal(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(SIMULATE_PROCESSING_MODAL_KEY, 'true');
    } else {
      localStorage.removeItem(SIMULATE_PROCESSING_MODAL_KEY);
    }
    this.simulateProcessingModal = enabled;
  }

  private syncMobileUIClass() {
    document.documentElement.classList.toggle('mobile-ui', this.mobileUI);
  }

  @action
  setMobileUI(enabled: boolean) {
    localStorage.setItem(MOBILE_UI_KEY, enabled ? 'true' : 'false');
    this.mobileUI = enabled;
  }

  @action
  setShowCenterLine(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(SHOW_CENTER_LINE_KEY, 'true');
    } else {
      localStorage.removeItem(SHOW_CENTER_LINE_KEY);
    }
    this.showCenterLine = enabled;
  }

  @action
  setForceFirstTimeUser(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(FORCE_FIRST_TIME_USER_KEY, 'true');
    } else {
      localStorage.removeItem(FORCE_FIRST_TIME_USER_KEY);
    }
    this.forceFirstTimeUser = enabled;
  }

  @computed
  get showUploadStatus(): boolean {
    if (FORCE_UPLOAD_STATUS_ENABLED) return true;
    return this.respectfulDebugMode && this.uploadStatus;
  }

  /**
   * Story Atlas access: team email domains and allowlisted subscriptions get
   * it unconditionally; everyone else needs debug mode plus the explicit
   * toggle. Note the subscription leg depends on billing having loaded —
   * gate consumers that navigate (vs merely hide UI) should wait for
   * `billingStore.data` before acting on a `false`. A billing *error* fails
   * open (matching the paywall's convention): we can't rule the subscription
   * leg out, and bouncing a paying subscriber over a transient billing
   * failure is worse than briefly showing a gated beta to a free user.
   */
  @computed
  get showStoryAtlas(): boolean {
    if (this.isTeamMember) return true;
    const productId = billingStore.data?.subscription?.product?.id;
    if (productId && ATLAS_SUBSCRIPTION_PRODUCT_IDS.includes(productId)) return true;
    if (!billingStore.data && billingStore.error) return true;
    return this.respectfulDebugMode && this.storyAtlas;
  }

  /** Whether the publishing entry points carry the "internal testing" badge. Constant until the launch flips it. */
  get publishingInternalOnly(): boolean {
    return PUBLISHING_INTERNAL_ONLY;
  }

  /**
   * Whether the Schedule nav row and the Settings Publishing tab render.
   * Everyone once the launch flips `PUBLISHING_INTERNAL_ONLY`; until then
   * team email domains only, minus those previewing the public view. The
   * preview only counts in debug mode, as the sibling toggles do, so
   * leaving debug mode brings the entry points back. `null` from
   * `isTeamMember` (user not loaded) hides: nothing redirects on this, so
   * a moment without the row costs nothing and shows nothing early.
   */
  @computed
  get showPublishingEntryPoints(): boolean {
    if (!PUBLISHING_INTERNAL_ONLY) return true;
    if (this.isTeamMember !== true) return false;
    return !this.respectfulDebugMode || this.publishingEntryPoints;
  }

  @action
  setPublishingEntryPoints(enabled: boolean) {
    if (enabled) {
      localStorage.removeItem(PUBLISHING_ENTRY_POINTS_KEY);
    } else {
      localStorage.setItem(PUBLISHING_ENTRY_POINTS_KEY, 'false');
    }
    this.publishingEntryPoints = enabled;
  }

  @action
  setStoryAtlas(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(STORY_ATLAS_KEY, 'true');
    } else {
      localStorage.removeItem(STORY_ATLAS_KEY);
    }
    this.storyAtlas = enabled;
  }

  @action
  setUploadStatus(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(UPLOAD_STATUS_KEY, 'true');
    } else {
      localStorage.removeItem(UPLOAD_STATUS_KEY);
    }
    this.uploadStatus = enabled;
  }

  @action
  setMediaCardsDebugMode(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(DEBUG_MEDIA_CARDS_KEY, 'true');
    } else {
      localStorage.removeItem(DEBUG_MEDIA_CARDS_KEY);
    }
    this.mediaCardsDebugMode = enabled;
  }

  @action
  setFakePodcastClipCount(count: number | null) {
    if (count === null) {
      localStorage.removeItem(FAKE_PODCAST_CLIP_COUNT_KEY);
    } else {
      localStorage.setItem(FAKE_PODCAST_CLIP_COUNT_KEY, String(count));
    }
    this.fakePodcastClipCount = count;
  }

  @action
  setEnableUnifiedSearch(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(ENABLE_UNIFIED_SEARCH_KEY, 'true');
    } else {
      localStorage.removeItem(ENABLE_UNIFIED_SEARCH_KEY);
    }
    this.enableUnifiedSearch = enabled;
  }

  @action
  setGridRankDebugMode(enabled: boolean) {
    if (enabled) {
      localStorage.setItem(GRID_RANK_DEBUG_KEY, 'true');
    } else {
      localStorage.removeItem(GRID_RANK_DEBUG_KEY);
    }
    this.gridRankDebugMode = enabled;
  }

  @action
  handleDebugClick(onSuccess?: () => void) {
    const now = Date.now();
    this.clickTimestamps.push(now);

    // Clear any existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // If we have 7 clicks, check the pattern
    if (this.clickTimestamps.length === 7) {
      const intervals: number[] = [];
      for (let i = 1; i < this.clickTimestamps.length; i++) {
        intervals.push(this.clickTimestamps[i] - this.clickTimestamps[i - 1]);
      }

      // Calculate average interval to determine relative timing
      const averageInterval =
        intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;

      // Pattern: short, short, short, short, long, short
      const isShort = (interval: number) => {
        const ratio = interval / averageInterval;
        return ratio >= 0.1 && ratio <= 1.5;
      };
      const isLong = (interval: number) => {
        const ratio = interval / averageInterval;
        return ratio >= 1.5 && ratio <= 5;
      };

      const pattern = [
        isShort(intervals[0]), // 1st interval: short
        isShort(intervals[1]), // 2nd interval: short
        isShort(intervals[2]), // 3rd interval: short
        isShort(intervals[3]), // 4th interval: short
        isLong(intervals[4]), // 5th interval: long
        isShort(intervals[5]), // 6th interval: short
      ];

      if (pattern.every(Boolean)) {
        this.setRespectfulDebugMode(true);
        this.clickTimestamps = [];
        onSuccess?.();
      } else {
        // Log the detected pattern with dots (short) and dashes (long)
        const patternString = intervals
          .map((interval) => {
            if (isShort(interval)) return '.';
            if (isLong(interval)) return '-';
            return '?';
          })
          .join('');
        console.log(patternString);
        // Reset if pattern doesn't match
        this.clickTimestamps = [now];
      }
    } else if (this.clickTimestamps.length > 7) {
      // Reset if too many clicks
      this.clickTimestamps = [now];
    }

    // Reset if no clicks for 2 seconds
    this.timeout = setTimeout(() => {
      this.clickTimestamps = [];
    }, 2000);
  }
}

// singleton, as we don't need multiple instances
export const debugStore = new DebugStore();
